import { supabase } from "@/integrations/supabase/client";
import { getS3UploadUrl } from "@/lib/s3.functions";

/**
 * Resolve a stored image reference to a public URL.
 * Handles full URLs (S3, external CDN) and Supabase Storage paths in the "looks" bucket.
 */
export function lookImageUrl(pathOrUrl: string | null | undefined): string {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return supabase.storage.from("looks").getPublicUrl(pathOrUrl).data.publicUrl;
}

function extFromFile(file: File): string {
  const t = file.type;
  if (t === "image/png") return "png";
  if (t === "image/webp") return "webp";
  if (t === "image/gif") return "gif";
  if (t === "video/mp4") return "mp4";
  if (t === "video/webm") return "webm";
  return "jpg";
}

/**
 * Upload a user image. Tries AWS S3 first (when configured server-side);
 * falls back to Supabase Storage "looks" bucket if S3 isn't set up.
 * Returns a string suitable for storing in image_path / avatar_url / cover_url:
 * - S3: a full https:// URL
 * - Supabase fallback: a bucket path (resolve with lookImageUrl)
 */
export async function uploadLookImage(file: File, prefix = "looks"): Promise<string> {
  const ext = extFromFile(file);
  try {
    const { uploadUrl, publicUrl } = await getS3UploadUrl({
      data: { prefix, ext, contentType: file.type || "application/octet-stream" },
    });
    const put = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!put.ok) throw new Error(`S3 PUT failed (${put.status})`);
    return publicUrl;
  } catch (s3err) {
    // Fallback: Supabase Storage
    const { data: u } = await supabase.auth.getUser();
    const uid = u.user?.id;
    if (!uid) throw s3err instanceof Error ? s3err : new Error("Not signed in");
    const path = `${uid}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("looks").upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type,
    });
    if (error) throw error;
    return path;
  }
}

/** Upload with a fixed key (e.g. avatar/cover replace-in-place). */
export async function uploadLookImageAtKey(file: File, fixedKey: string): Promise<string> {
  const ext = extFromFile(file);
  try {
    const { uploadUrl, publicUrl } = await getS3UploadUrl({
      data: { prefix: "profile", ext, contentType: file.type || "application/octet-stream" },
    });
    const put = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!put.ok) throw new Error(`S3 PUT failed (${put.status})`);
    return `${publicUrl}?v=${Date.now()}`;
  } catch {
    const path = `${fixedKey}.${ext}`;
    const { error } = await supabase.storage.from("looks").upload(path, file, { upsert: true, contentType: file.type });
    if (error) throw error;
    const url = supabase.storage.from("looks").getPublicUrl(path).data.publicUrl;
    return `${url}?v=${Date.now()}`;
  }
}
